/* global Modal */

if (!customElements.get('voer-keuzehulp')) {
  customElements.whenDefined('modal-dialog').then(() => {
    const PACK_QUESTION_IDS = ['pack_size', 'verpakking'];
    const SMALL_PATTERN = /small|klein/i;
    const LARGE_PATTERN = /large|groot/i;

    class VoerKeuzehulp extends Modal {
      constructor() {
        super();

        this.quizData = null;
        const dataEl = this.querySelector('[data-voer-keuzehulp-data]');
        if (dataEl) {
          try {
            this.quizData = JSON.parse(dataEl.textContent);
          } catch (e) {
            this.quizData = null;
          }
        }

        this.views = {
          intro: this.querySelector('[data-vk-view="intro"]'),
          questions: this.querySelector('[data-vk-view="questions"]'),
          result: this.querySelector('[data-vk-view="result"]')
        };
        this.steps = Array.from(this.querySelectorAll('[data-vk-step]'));
        this.progressEl = this.querySelector('[data-vk-progress]');
        this.progressFill = this.querySelector('[data-vk-progress-fill]');
        this.prevBtn = this.querySelector('.js-vk-prev');
        this.nextBtn = this.querySelector('.js-vk-next');
        this.currentStep = 0;

        const startBtn = this.querySelector('.js-vk-start');
        if (startBtn) startBtn.addEventListener('click', this.start.bind(this));
        if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlePrev.bind(this));
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleNext.bind(this));

        const restartBtn = this.querySelector('.js-vk-restart');
        if (restartBtn) restartBtn.addEventListener('click', this.restart.bind(this));

        this.addEventListener('change', (evt) => {
          if (evt.target.matches('.voer-keuzehulp__option-input')) {
            const hint = this.steps[this.currentStep]
              && this.steps[this.currentStep].querySelector('[data-vk-required-hint]');
            if (hint) hint.hidden = true;
          }
        });

        this.addEventListener('click', (evt) => {
          const link = evt.target.closest('.js-vk-product-link');
          if (link) {
            this.track('dog_food_product_click', {
              quiz: this.dataset.quizKey,
              url: link.getAttribute('href'),
              result_key: this.lastResultKey || null
            });
          }
        });

        this.addEventListener('on:cart:add', (evt) => {
          this.track('dog_food_add_to_cart', {
            quiz: this.dataset.quizKey,
            variant_id: evt.detail && evt.detail.variantId,
            result_key: this.lastResultKey || null
          });
          // Close the quiz so the cart drawer is usable on top.
          setTimeout(() => this.close(), 300);
        });

        if (this.hasAttribute('data-open-on-hash') && window.location.hash === '#keuzehulp') {
          const opener = document.querySelector(`modal-opener[data-modal="${this.id}"] button`);
          this.open(opener);
        }
      }

      open(opener) {
        super.open(opener);
        this.track('dog_food_quiz_start', { quiz: this.dataset.quizKey });
      }

      start() {
        this.showView('questions');
        this.goToStep(0);
      }

      showView(name) {
        Object.keys(this.views).forEach((key) => {
          if (this.views[key]) this.views[key].hidden = key !== name;
        });
      }

      goToStep(index) {
        this.currentStep = index;
        this.steps.forEach((step, i) => {
          step.hidden = i !== index;
        });

        if (this.prevBtn) this.prevBtn.hidden = index === 0;
        if (this.nextBtn) {
          this.nextBtn.textContent = index === this.steps.length - 1
            ? this.nextBtn.dataset.finishLabel
            : this.nextBtn.dataset.nextLabel;
        }

        const template = this.dataset.progressTemplate || '[current]/[total]';
        if (this.progressEl) {
          this.progressEl.textContent = template
            .replace('[current]', index + 1)
            .replace('[total]', this.steps.length);
        }
        if (this.progressFill) {
          this.progressFill.style.width = `${((index + 1) / this.steps.length) * 100}%`;
        }

        const step = this.steps[index];
        if (step) {
          step.setAttribute('tabindex', '-1');
          step.focus({ preventScroll: false });
        }
      }

      stepValues(step) {
        return Array.from(step.querySelectorAll('.voer-keuzehulp__option-input:checked'))
          .map((input) => input.value);
      }

      handleNext() {
        const step = this.steps[this.currentStep];
        if (!step) return;

        const values = this.stepValues(step);
        if (step.hasAttribute('data-required') && values.length === 0) {
          const hint = step.querySelector('[data-vk-required-hint]');
          if (hint) hint.hidden = false;
          return;
        }

        this.track('dog_food_quiz_answer', {
          quiz: this.dataset.quizKey,
          question: step.dataset.questionKey,
          answer: values
        });

        if (this.currentStep === this.steps.length - 1) {
          this.finish();
        } else {
          this.goToStep(this.currentStep + 1);
        }
      }

      handlePrev() {
        if (this.currentStep > 0) this.goToStep(this.currentStep - 1);
      }

      collectAnswers() {
        const answers = {};
        this.steps.forEach((step) => {
          answers[step.dataset.questionKey] = this.stepValues(step);
        });
        return answers;
      }

      /**
       * Picks the best matching rule. A rule matches when, for every key in
       * rule.match, the given answers intersect the allowed values (AND across
       * questions, OR within values). Score = number of matched keys; the
       * highest score wins, ties resolve to the first rule in the array.
       */
      score(answers) {
        if (!this.quizData || !Array.isArray(this.quizData.rules)) return null;

        let best = null;
        this.quizData.rules.forEach((rule, index) => {
          const match = rule.match || {};
          const keys = Object.keys(match);
          if (keys.length === 0) return;

          let qualified = true;
          let ruleScore = 0;
          keys.forEach((key) => {
            const allowed = Array.isArray(match[key]) ? match[key] : [];
            const given = Array.isArray(answers[key]) ? answers[key] : [];
            if (given.some((value) => allowed.includes(value))) {
              ruleScore += 1;
            } else {
              qualified = false;
            }
          });

          if (qualified && (!best || ruleScore > best.score)) {
            best = { rule, score: ruleScore, index };
          }
        });
        return best;
      }

      packPreference(answers) {
        for (let i = 0; i < PACK_QUESTION_IDS.length; i += 1) {
          const values = answers[PACK_QUESTION_IDS[i]];
          if (Array.isArray(values) && values.length > 0) {
            if (values.some((value) => SMALL_PATTERN.test(value))) return 'small';
            if (values.some((value) => LARGE_PATTERN.test(value))) return 'large';
            return null;
          }
        }
        return null;
      }

      static parseKg(text) {
        if (!text) return null;
        const match = /([\d]+(?:[.,]\d+)?)\s*kg/i.exec(text);
        if (!match) return null;
        return parseFloat(match[1].replace(',', '.'));
      }

      finish() {
        const answers = this.collectAnswers();
        let best = this.score(answers);

        // No matching rule: fall back to the rule flagged with "fallback": true, if any.
        if (!best && this.quizData && Array.isArray(this.quizData.rules)) {
          const fallbackIndex = this.quizData.rules.findIndex((rule) => rule.fallback === true);
          if (fallbackIndex !== -1) {
            best = { rule: this.quizData.rules[fallbackIndex], score: 0, index: fallbackIndex };
          }
        }

        this.track('dog_food_quiz_completed', { quiz: this.dataset.quizKey, answers });
        this.showView('result');

        const panels = this.querySelectorAll('.voer-keuzehulp__result');
        panels.forEach((panel) => { panel.hidden = true; });
        const fallback = this.querySelector('[data-vk-fallback]');

        let panel = null;
        if (best && best.rule.id) {
          panel = this.querySelector(`.voer-keuzehulp__result[data-result-key="${CSS.escape(best.rule.id)}"]`);
        }

        if (!panel) {
          if (fallback) fallback.hidden = false;
          this.lastResultKey = null;
          this.track('dog_food_quiz_result', { quiz: this.dataset.quizKey, result_key: null });
          return;
        }

        const hasProducts = this.renderPanel(panel, answers);
        if (!hasProducts) {
          if (fallback) fallback.hidden = false;
          this.lastResultKey = null;
          this.track('dog_food_quiz_result', { quiz: this.dataset.quizKey, result_key: null });
          return;
        }

        this.lastResultKey = best.rule.id;
        panel.hidden = false;
        this.track('dog_food_quiz_result', {
          quiz: this.dataset.quizKey,
          result_key: best.rule.id,
          score: best.score
        });

        const scroller = this.querySelector('.voer-keuzehulp__scroll');
        if (scroller) scroller.scrollTop = 0;
        const window_ = this.querySelector('.voer-keuzehulp__window');
        if (window_) window_.focus();
      }

      renderPanel(panel, answers) {
        this.renderWhyBullets(panel);

        const productsContainer = panel.querySelector('[data-vk-products]');
        const alternative = panel.querySelector('[data-vk-alternative]');
        const altSlot = panel.querySelector('[data-vk-alt-slot]');

        let wrappers = Array.from(panel.querySelectorAll('[data-vk-product]'))
          .filter((wrapper) => wrapper.querySelector('.voer-keuzehulp__card'));

        wrappers.forEach((wrapper) => {
          wrapper.hidden = true;
          if (productsContainer && wrapper.parentElement !== productsContainer) {
            productsContainer.appendChild(wrapper);
          }
        });

        const pack = this.packPreference(answers);
        if (pack) {
          wrappers = wrappers
            .map((wrapper, index) => ({
              wrapper,
              index,
              kg: VoerKeuzehulp.parseKg(wrapper.dataset.productTitle)
            }))
            .sort((a, b) => {
              if (a.kg === null && b.kg === null) return a.index - b.index;
              if (a.kg === null) return 1;
              if (b.kg === null) return -1;
              return pack === 'small' ? a.kg - b.kg : b.kg - a.kg;
            })
            .map((item) => item.wrapper);
        }

        if (wrappers[0]) {
          wrappers[0].hidden = false;
          this.preselectVariant(wrappers[0], pack);
        }

        if (alternative) {
          if (wrappers[1] && altSlot) {
            altSlot.appendChild(wrappers[1]);
            wrappers[1].hidden = false;
            this.preselectVariant(wrappers[1], pack);
            alternative.hidden = false;
            alternative.removeAttribute('open');
          } else {
            alternative.hidden = true;
          }
        }

        return wrappers.length > 0;
      }

      renderWhyBullets(panel) {
        const why = panel.querySelector('[data-vk-why]');
        const list = panel.querySelector('[data-vk-why-list]');
        if (!why || !list) return;

        const labels = [];
        this.steps.forEach((step) => {
          if (PACK_QUESTION_IDS.includes(step.dataset.questionKey)) return;
          step.querySelectorAll('.voer-keuzehulp__option-input:checked').forEach((input) => {
            const labelEl = input.parentElement.querySelector('.voer-keuzehulp__option-label');
            if (labelEl) labels.push(labelEl.textContent.trim());
          });
        });

        list.innerHTML = '';
        labels.slice(0, 3).forEach((label) => {
          const li = document.createElement('li');
          li.textContent = label;
          list.appendChild(li);
        });
        why.hidden = labels.length === 0;
      }

      preselectVariant(wrapper, pack) {
        if (!pack) return;
        const select = wrapper.querySelector('.js-vk-variant-select');
        if (!select) return;

        const options = Array.from(select.options)
          .filter((option) => !option.disabled)
          .map((option) => ({
            option,
            kg: VoerKeuzehulp.parseKg(option.dataset.variantTitle)
              || (parseFloat(option.dataset.weightGrams) > 0
                ? parseFloat(option.dataset.weightGrams) / 1000
                : null)
          }))
          .filter((item) => item.kg !== null);
        if (options.length === 0) return;

        options.sort((a, b) => (pack === 'small' ? a.kg - b.kg : b.kg - a.kg));
        if (select.value !== options[0].option.value) {
          select.value = options[0].option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      restart() {
        this.querySelectorAll('.voer-keuzehulp__option-input').forEach((input) => {
          input.checked = false;
        });
        this.querySelectorAll('.voer-keuzehulp__result').forEach((panel) => {
          panel.hidden = true;
        });
        const fallback = this.querySelector('[data-vk-fallback]');
        if (fallback) fallback.hidden = true;
        this.showView('questions');
        this.goToStep(0);
      }

      /**
       * Tracking stub. Events are intentionally not wired up yet; hook a
       * dataLayer push or analytics call in here later.
       */
      track(eventName, payload = {}) {
        if (window.voerKeuzehulpDebug) {
          console.debug('[voer-keuzehulp]', eventName, payload);
        }
      }
    }

    customElements.define('voer-keuzehulp', VoerKeuzehulp);
  });
}
